<template>
    <div>
        <ModeSwitcher
            ref="modeSwitcher"
            :main-mode-text="$t('create')"
            :create-mode-text="
                serviceType === contractServiceType.ownCarRent
                    ? $t('product.info.create_driver')
                    : $t('product.info.create_guest_category')
            "
            :edit-mode-text="
                serviceType === contractServiceType.ownCarRent
                    ? $t('product.info.edit_driver')
                    : $t('product.info.edit_guest_category')
            "
            @reset="editGuestCategory = null"
        >
            <template v-slot:btn-block>
                <div class="full-width d-flex align-center">
                    <v-spacer />
                    <FilterStatusToggle v-model="activeFilter" />
                </div>
            </template>
            <template v-slot:main-mode>
                <v-data-table
                    :headers="useHeaders"
                    :items="useFilteredPaxes"
                    :items-per-page="-1"
                    :items-per-page-options="[-1]"
                    :items-length="useFilteredPaxes.length"
                    :custom-key-sort="{
                        name: (a, b) => localized(a).localeCompare(localized(b)),
                    }"
                    :sort-by="[{key: 'name', order: 'asc'}]"
                    must-sort
                    density="compact"
                    color="primary"
                    class="elevation-1 table-server"
                    data-cy="guests-table"
                >
                    <template v-slot:item.name="{item}">
                        <div class="text-link" @click="editCategory(item)">
                            {{ localized(item.name) }}
                        </div>
                        <div v-if="item.isDefault" class="text-medium-emphasis">
                            ({{ $t('product.info.default_value') }})
                        </div>
                    </template>
                    <template v-slot:item.category="{item}">
                        {{ $t(`guestCategory.${item.category}`) }}
                    </template>
                    <template v-slot:item.active="{item}">
                        {{ item.active ? $t('active') : $t('inactive') }}
                    </template>
                </v-data-table>
            </template>
            <template v-slot:additional-mode>
                <InfoGuestsCategoriesForm
                    :guests-category="editGuestCategory"
                    :contract-id="contractId"
                    :service-type="serviceType"
                    @save="save()"
                    @cancel="modeSwitcher.goToTable()"
                />
            </template>
        </ModeSwitcher>
    </div>
</template>

<script setup lang="ts">
    import InfoGuestsCategoriesForm from '~/components/products/information/InfoGuestsCategoriesForm.vue'
    import {contractServiceType} from '~/utils/types/types'

    const localized = useLocalized()

    type HeaderItemType = {
        title: string
        value: string
        width?: string
        minWidth?: string
    }

    const props = defineProps<{
        contractId: number
        serviceType?: string
    }>()

    const guestsCategories = ref<any[]>([])
    const modeSwitcher = ref(null)
    const editGuestCategory = ref(null)
    const activeFilter = ref<boolean>(true)

    const {t} = useI18n()
    const app = useNuxtApp()

    onMounted(async () => {
        await loadData()
    })

    async function loadData(): Promise<void> {
        try {
            const rq = {contractId: props.contractId}
            const res = await app.$api.ownProductPaxes.get(rq)
            guestsCategories.value = res.paxes
        } catch (e) {
            guestsCategories.value = []
        }
    }

    const save = async (): Promise<void> => {
        await loadData()
        modeSwitcher.value.goToTable()
    }

    const useHeaders: ComputedRef<HeaderItemType[]> = computed(() => {
        const headers: HeaderItemType[] = [
            {title: t('name'), value: 'name', minWidth: '120px', sortable: true},
            {title: t('product.info.minimal_age'), value: 'ageFrom'},
            {title: t('product.info.maximal_age'), value: 'ageTo'},
            {title: t('category'), value: 'category'},
            {title: t('status'), value: 'active', width: '140px', minWidth: '140px'},
        ]
        return headers
    })

    const editCategory = (category): void => {
        modeSwitcher.value.edit()
        editGuestCategory.value = category
    }

    const useFilteredPaxes = computed(() => {
        return guestsCategories.value.filter(({active}) => activeFilter.value === active)
    })
</script>
